import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import swal from "sweetalert";
import CountryPhoneInput from "../shared/CountryPhoneInput";
import PageTitle from "../../layouts/PageTitle";
import { addRestaurantAdmin } from "../../../services/RestaurantAdminsService";
import "./RestaurantUsers.css";

const tr = (t, key, fallback) => {
  if (!t || typeof t !== "function") return fallback;
  const res = t(key);
  return !res || res === key ? fallback : res;
};

const getInitials = (firstName = "", lastName = "") => {
  const f = String(firstName || "").trim().charAt(0);
  const l = String(lastName || "").trim().charAt(0);
  const initials = `${f}${l}`.toUpperCase();
  return initials || "?";
};

const AVAILABLE_MODULES = [
  {
    id: "home",
    icon: "fas fa-home",
    nameKey: "moduleHomeName",
    defaultName: "Home & Dashboard",
    descKey: "moduleHomeDesc",
    defaultDesc: "Main operational dashboard, key performance metrics and overview.",
  },
  {
    id: "restaurant-info",
    icon: "fas fa-store",
    nameKey: "moduleRestaurantInfoName",
    defaultName: "Restaurant Information",
    descKey: "moduleRestaurantInfoDesc",
    defaultDesc: "Restaurant profile, opening hours, addresses, settings & contacts.",
  },
  {
    id: "pos",
    icon: "fas fa-cash-register",
    nameKey: "modulePosName",
    defaultName: "Point of Sale & Orders",
    descKey: "modulePosDesc",
    defaultDesc: "Live orders, kitchen board, and table reservations.",
  },
  {
    id: "kitchen",
    icon: "fas fa-utensils",
    nameKey: "moduleKitchenName",
    defaultName: "Kitchen & Menu",
    descKey: "moduleKitchenDesc",
    defaultDesc: "Menu list, categories, subcategories, products & choices.",
  },
  {
    id: "clients",
    icon: "fas fa-user-friends",
    nameKey: "moduleClientsName",
    defaultName: "Clients & Guests",
    descKey: "moduleClientsDesc",
    defaultDesc: "Customer directory, visit history & contact details.",
  },
  {
    id: "events",
    icon: "fas fa-bullhorn",
    nameKey: "moduleEventsName",
    defaultName: "Events & Promotions",
    descKey: "moduleEventsDesc",
    defaultDesc: "Special events, event bookings & promotional banners.",
  },
  {
    id: "support",
    icon: "fas fa-headset",
    nameKey: "moduleSupportName",
    defaultName: "Customer Support & Claims",
    descKey: "moduleSupportDesc",
    defaultDesc: "Support requests, reclamations, feedback & lost-found.",
  },
  {
    id: "qrcode",
    icon: "fas fa-qrcode",
    nameKey: "moduleQrName",
    defaultName: "QR Code Generator",
    descKey: "moduleQrDesc",
    defaultDesc: "Table QR codes creation and print exports.",
  },
  {
    id: "admin-history",
    icon: "fas fa-history",
    nameKey: "moduleHistoryName",
    defaultName: "Admin Action History",
    descKey: "moduleHistoryDesc",
    defaultDesc: "Backoffice audit logs and administrator activity history.",
  },
];

const AddRestaurantUser = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // User fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Module Access / Permissions selection (default: all selected)
  const [selectedPermissions, setSelectedPermissions] = useState(
    AVAILABLE_MODULES.map((m) => m.id)
  );

  const initials = getInitials(firstName, lastName);
  const fullNamePreview = `${firstName} ${lastName}`.trim() || tr(t, "restaurantUsersNewAdmin", "New User");

  const togglePermission = (id) => {
    setSelectedPermissions((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const selectAllPermissions = () => {
    setSelectedPermissions(AVAILABLE_MODULES.map((m) => m.id));
  };

  const clearAllPermissions = () => {
    setSelectedPermissions([]);
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!firstName.trim() || !lastName.trim() || !email.trim() || !phone.trim()) {
      swal(
        tr(t, "warning", "Warning"),
        tr(t, "restaurantUsersRequiredFields", "Please fill in all fields."),
        "warning"
      );
      return;
    }

    if (selectedPermissions.length === 0) {
      swal(
        tr(t, "warning", "Warning"),
        tr(t, "restaurantUsersSelectPermission", "Please select at least one module access permission for this user."),
        "warning"
      );
      return;
    }

    setSubmitting(true);
    try {
      const response = await addRestaurantAdmin({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        permissions: selectedPermissions,
        modules: selectedPermissions,
        accessPermissions: selectedPermissions,
      });

      if (response?.error) {
        swal(tr(t, "warning", "Warning"), response.error, "warning");
        return;
      }

      swal(
        tr(t, "success", "Success"),
        tr(t, "restaurantUsersCreateSuccess", "User created successfully. A welcome email with password was sent."),
        "success"
      );
      navigate("/restaurant-users");
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        tr(t, "restaurantUsersCreateError", "Failed to create user.");

      if (String(errorMessage).includes("email already exists")) {
        swal(
          tr(t, "warning", "Warning"),
          tr(t, "restaurantUsersEmailExists", "A user with this email already exists."),
          "warning"
        );
      } else {
        swal(tr(t, "warning", "Warning"), errorMessage, "warning");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageTitle
        activeMenu={tr(t, "restaurantUsersAddTitle", "Add User")}
        motherMenu={tr(t, "restaurantUsersTitle", "Users")}
      />

      <div className="ru-page">
        {/* Hero Header */}
        <header className="ru-hero">
          <div className="ru-hero__copy">
            <div className="ru-hero__icon-wrap">
              <i className="fas fa-user-plus" aria-hidden="true" />
            </div>
            <div className="ru-hero__text">
              <span className="ru-hero__eyebrow">
                <i className="fas fa-user-shield" aria-hidden="true" /> Backoffice
              </span>
              <h2 className="ru-hero__title">
                {tr(t, "restaurantUsersAddTitle", "Add User")}
              </h2>
              <p className="ru-hero__subtitle">
                {tr(t, "restaurantUsersAddSubtitle", "Grant access and define module permissions for a new team member.")}
              </p>
            </div>
          </div>
          <div className="ru-hero__actions">
            <Link to="/restaurant-users" className="ru-btn ru-btn-secondary">
              <i className="fas fa-arrow-left" aria-hidden="true" />
              <span>{tr(t, "back", "Back to Users")}</span>
            </Link>
          </div>
        </header>

        {/* Form & Live Preview Grid */}
        <div className="ru-form-grid">
          {/* Avatar Live Preview Side Card */}
          <div className="ru-avatar-preview-card">
            <div className="ru-avatar-preview-large">
              {initials}
            </div>
            <h4 className="ru-avatar-preview-name">{fullNamePreview}</h4>
            <span className="ru-avatar-preview-role">
              <i className="fas fa-shield-alt me-1 text-primary" />
              {tr(t, "restaurantUsersAdmin", "Admin")}
            </span>
            <div className="mt-3 pt-3 border-top w-100 text-center">
              <span className="ru-badge ru-badge--active mb-2 d-inline-flex">
                {tr(t, "restaurantUsersActive", "Active")}
              </span>
              <div>
                <span className="ru-count-badge">
                  <i className="fas fa-lock-open me-1 text-primary" />
                  {selectedPermissions.length} / {AVAILABLE_MODULES.length} Modules
                </span>
              </div>
            </div>
          </div>

          {/* Main Form Card */}
          <div className="ru-form-card">
            <div className="ru-form-header">
              <h3 className="ru-form-title">
                {tr(t, "restaurantUsersOwnerInformation", "User Information")}
              </h3>
              <p className="ru-form-desc">
                {tr(t, "restaurantUsersFormHint", "Enter personal details and configure backoffice module access.")}
              </p>
            </div>

            <div className="ru-form-body">
              <form onSubmit={onSubmit}>
                {/* Personal Information */}
                <div className="row">
                  <div className="col-md-6 ru-form-group">
                    <label className="ru-form-label">
                      {tr(t, "restaurantUsersColFirstName", "First name")} *
                    </label>
                    <div className="ru-input-wrap">
                      <i className="fas fa-user" aria-hidden="true" />
                      <input
                        type="text"
                        className="ru-form-control"
                        placeholder="e.g. Jean"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-6 ru-form-group">
                    <label className="ru-form-label">
                      {tr(t, "restaurantUsersColLastName", "Last name")} *
                    </label>
                    <div className="ru-input-wrap">
                      <i className="fas fa-user" aria-hidden="true" />
                      <input
                        type="text"
                        className="ru-form-control"
                        placeholder="e.g. Dupont"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-12 ru-form-group">
                    <label className="ru-form-label">
                      {tr(t, "restaurantUsersColEmail", "Email")} *
                    </label>
                    <div className="ru-input-wrap">
                      <i className="fas fa-envelope" aria-hidden="true" />
                      <input
                        type="email"
                        className="ru-form-control"
                        placeholder="e.g. admin@restaurant.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-12 ru-form-group">
                    <label className="ru-form-label">
                      {tr(t, "restaurantUsersColPhone", "Phone")} *
                    </label>
                    <CountryPhoneInput
                      value={phone}
                      onChange={(value) => setPhone(value)}
                      inputClassName="ru-form-control"
                      inputProps={{
                        name: "phone",
                        required: true,
                      }}
                      placeholder="+1 234 567 8900"
                    />
                  </div>
                </div>

                {/* Module Access & Permissions Section */}
                <div className="ru-perm-section">
                  <div className="ru-perm-header">
                    <div>
                      <h4 className="ru-perm-title">
                        <i className="fas fa-user-lock" aria-hidden="true" />
                        {tr(t, "moduleAccessTitle", "Module Access & Permissions")}
                      </h4>
                      <p className="ru-perm-subtitle">
                        {tr(
                          t,
                          "moduleAccessSubtitle",
                          "Select which backoffice sections this user is allowed to view and manage."
                        )}
                      </p>
                    </div>

                    <div className="ru-perm-actions">
                      <button
                        type="button"
                        className="ru-perm-btn-link"
                        onClick={selectAllPermissions}
                      >
                        {tr(t, "selectAll", "Select All")}
                      </button>
                      <span className="text-muted">|</span>
                      <button
                        type="button"
                        className="ru-perm-btn-link"
                        onClick={clearAllPermissions}
                      >
                        {tr(t, "deselectAll", "Clear All")}
                      </button>
                    </div>
                  </div>

                  <div className="ru-perm-grid">
                    {AVAILABLE_MODULES.map((mod) => {
                      const isChecked = selectedPermissions.includes(mod.id);
                      return (
                        <div
                          key={mod.id}
                          className={`ru-perm-card ${isChecked ? "is-selected" : ""}`}
                          onClick={() => togglePermission(mod.id)}
                        >
                          <div className="ru-perm-card__icon">
                            <i className={mod.icon} aria-hidden="true" />
                          </div>
                          <div className="ru-perm-card__content">
                            <span className="ru-perm-card__name">
                              {tr(t, mod.nameKey, mod.defaultName)}
                            </span>
                            <span className="ru-perm-card__desc">
                              {tr(t, mod.descKey, mod.defaultDesc)}
                            </span>
                          </div>
                          <div className="ru-perm-switch">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {}} // handled by div click
                            />
                            <span className="ru-perm-slider" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="ru-info-alert">
                  <i className="fas fa-info-circle" aria-hidden="true" />
                  <div>
                    <strong>{tr(t, "note", "Note")}:</strong>{" "}
                    {tr(
                      t,
                      "restaurantUsersEmailNotice",
                      "Once created, a welcome email containing instructions and a temporary login password will be sent directly to this user's email."
                    )}
                  </div>
                </div>

                <div className="ru-form-actions">
                  <button
                    type="button"
                    className="ru-btn ru-btn-secondary"
                    onClick={() => navigate("/restaurant-users")}
                    disabled={submitting}
                  >
                    {tr(t, "cancel", "Cancel")}
                  </button>
                  <button
                    type="submit"
                    className="ru-btn ru-btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                          aria-hidden="true"
                        />
                        {tr(t, "loading", "Creating...")}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check me-1" aria-hidden="true" />
                        {tr(t, "restaurantUsersFinishCreation", "Create User")}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddRestaurantUser;
